<template>
  <header class="nv2" :class="{ 'nv2--scrolled': scrolled }">
    <div class="nv2-bar">
      <!-- logo -->
      <router-link to="/" class="nv2-logo" aria-label="iSamurai home">
        <img :src="logoSrc" alt="iSamurai" />
      </router-link>

      <!-- desktop nav -->
      <nav class="nv2-desktop" aria-label="Main">
        <router-link to="/" class="nv2-link" active-class="nv2-link--on" exact>{{ $t('navbar.home') }}</router-link>

        <div class="nv2-menu-wrap" ref="createWrap">
          <button
            class="nv2-link nv2-create-btn"
            :class="{ 'nv2-link--on': createOpen || createActive }"
            aria-haspopup="true"
            :aria-expanded="createOpen ? 'true' : 'false'"
            @click.stop="createOpen = !createOpen"
          >
            ✨ Create
            <span class="nv2-caret" :class="{ 'nv2-caret--open': createOpen }">▾</span>
          </button>
          <transition name="nv2-pop">
            <ul v-if="createOpen" class="nv2-pop" role="menu">
              <li v-for="item in createItems" :key="item.to" role="none">
                <hr v-if="item.divider" class="nv2-sep" />
                <router-link
                  v-else
                  :to="item.to"
                  role="menuitem"
                  class="nv2-pop-item"
                  :class="{ 'nv2-pop-item--on': $route.path === item.to }"
                  @click="createOpen = false"
                >
                  <span class="nv2-emoji">{{ item.emoji }}</span>
                  <span class="nv2-pop-label">{{ item.label }}</span>
                  <span v-if="item.isNew" class="nv2-new">NEW</span>
                </router-link>
              </li>
            </ul>
          </transition>
        </div>
      </nav>

      <div class="nv2-spacer"></div>

      <!-- right cluster -->
      <div class="nv2-right">
        <router-link
          v-if="loggedIn"
          :to="lowCredits ? '/subscriptions' : '/profile'"
          class="nv2-credits"
          :class="{ 'nv2-credits--low': lowCredits }"
          :title="lowCredits ? $t('navbar.credits.topUp') : $t('navbar.credits.label')"
        >
          <span class="nv2-coin">🪙</span>
          <b>{{ userCredits }}</b>
          <em v-if="lowCredits" class="nv2-topup">{{ $t('navbar.credits.topUp') }}</em>
        </router-link>

        <button class="nv2-icon-btn nv2-desktop-only" :title="$t('navbar.language.tooltip')" @click="langModalOpen = true">
          🌐 <span class="nv2-lang-code">{{ currentLangCode }}</span>
        </button>

        <router-link v-if="loggedIn" to="/support-center" class="nv2-icon-btn nv2-desktop-only nv2-support" :title="$t('navbar.support')" @click="fetchSupportUnread">
          🎧
          <span v-if="supportUnreadCount > 0" class="nv2-badge">{{ supportUnreadCount }}</span>
        </router-link>

        <!-- profile (desktop) -->
        <div v-if="loggedIn" class="nv2-menu-wrap nv2-desktop-only" ref="profileWrap">
          <button
            class="nv2-avatar-btn"
            aria-haspopup="true"
            :aria-expanded="profileOpen ? 'true' : 'false'"
            aria-label="Account menu"
            @click.stop="profileOpen = !profileOpen"
          >
            {{ initial }}
          </button>
          <transition name="nv2-pop">
            <div v-if="profileOpen" class="nv2-pop nv2-pop--right">
              <div class="nv2-acct">
                <span class="nv2-acct-avatar">{{ initial }}</span>
                <span class="nv2-acct-info">
                  <b>{{ userInfo?.username || 'Guest' }}</b>
                  <span class="nv2-acct-meta">
                    <em class="nv2-plan">{{ planName || 'Free' }}</em>
                    <em class="nv2-acct-cr">🪙 {{ userCredits }}</em>
                  </span>
                </span>
              </div>
              <router-link to="/profile" class="nv2-pop-item" @click="profileOpen = false">👤 <span class="nv2-pop-label">{{ $t('navbar.profile') }}</span></router-link>
              <a href="https://www.reddit.com/r/iSamurai_face_swap/" target="_blank" rel="noopener noreferrer" class="nv2-promo">
                📣 Join for PromoCodes &amp; Updates
              </a>
              <a href="https://greazy.ai" target="_blank" rel="noopener noreferrer" class="nv2-promo nv2-promo--greazy">
                ✨ {{ $t('navbar.greazyPromo', 'Be more creative, with more freedom on Greazy AI — AI chat and unrestricted AI tools. Click here') }}
              </a>
              <button class="nv2-pop-item nv2-logout" @click="logout">⏻ <span class="nv2-pop-label">{{ $t('navbar.logout') }}</span></button>
            </div>
          </transition>
        </div>

        <!-- hamburger (mobile) -->
        <button
          class="nv2-burger nv2-mobile-only"
          aria-label="Menu"
          :aria-expanded="drawerOpen ? 'true' : 'false'"
          @click="openDrawer"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
    </div>

    <!-- ============ MOBILE DRAWER ============ -->
    <Teleport to="body">
    <transition name="nv2-fade">
      <div v-if="drawerOpen" class="nv2-veil" @click.self="closeDrawer">
        <aside class="nv2-drawer" role="dialog" aria-label="Menu">
          <div class="nv2-drawer-head">
            <img :src="logoSrc" alt="iSamurai" class="nv2-drawer-logo" />
            <button class="nv2-x" aria-label="Close" @click="closeDrawer">✕</button>
          </div>

          <div v-if="loggedIn" class="nv2-acct nv2-acct--drawer">
            <span class="nv2-acct-avatar">{{ initial }}</span>
            <span class="nv2-acct-info">
              <b>{{ userInfo?.username || 'Guest' }}</b>
              <span class="nv2-acct-meta">
                <em class="nv2-plan">{{ planName || 'Free' }}</em>
                <router-link :to="lowCredits ? '/subscriptions' : '/profile'" class="nv2-acct-cr" :class="{ 'nv2-acct-cr--low': lowCredits }" @click="closeDrawer">
                  🪙 {{ userCredits }}<template v-if="lowCredits"> · {{ $t('navbar.credits.topUp') }}</template>
                </router-link>
              </span>
            </span>
          </div>

          <p class="nv2-drawer-title">Create</p>
          <nav class="nv2-drawer-list">
            <template v-for="item in createItems" :key="'d' + item.to">
              <hr v-if="item.divider" class="nv2-sep" />
              <router-link
                v-else
                :to="item.to"
                class="nv2-drawer-item"
                :class="{ 'nv2-drawer-item--on': $route.path === item.to }"
                @click="closeDrawer"
              >
                <span class="nv2-emoji">{{ item.emoji }}</span>
                <span class="nv2-pop-label">{{ item.label }}</span>
                <span v-if="item.isNew" class="nv2-new">NEW</span>
              </router-link>
            </template>
          </nav>

          <hr class="nv2-sep" />
          <nav class="nv2-drawer-list">
            <router-link to="/profile" class="nv2-drawer-item" @click="closeDrawer">👤 <span class="nv2-pop-label">{{ $t('navbar.profile') }}</span></router-link>
            <button class="nv2-drawer-item" @click="langModalOpen = true">🌐 <span class="nv2-pop-label">{{ $t('navbar.language.label') }}: {{ currentLangCode }}</span></button>
          </nav>

          <a href="https://www.reddit.com/r/iSamurai_face_swap/" target="_blank" rel="noopener noreferrer" class="nv2-promo nv2-promo--drawer">
            📣 Join for PromoCodes &amp; Updates
          </a>
          <a href="https://greazy.ai" target="_blank" rel="noopener noreferrer" class="nv2-promo nv2-promo--drawer nv2-promo--greazy">
            ✨ {{ $t('navbar.greazyPromo', 'Be more creative, with more freedom on Greazy AI — AI chat and unrestricted AI tools. Click here') }}
          </a>

          <button v-if="loggedIn" class="nv2-drawer-item nv2-logout" @click="logout">⏻ <span class="nv2-pop-label">{{ $t('navbar.logout') }}</span></button>
        </aside>
      </div>
    </transition>
    </Teleport>

    <LanguageSwitcher v-model:open="langModalOpen" />
  </header>
</template>

<script>
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';

export default {
  name: 'Navbar2',
  components: { LanguageSwitcher },
  data() {
    return {
      createOpen: false,
      profileOpen: false,
      drawerOpen: false,
      langModalOpen: false,
      scrolled: false,
      supportUnreadCount: 0,
      logoSrc: require('@/assets/isamurai_logo.png'),
    };
  },
  computed: {
    loggedIn() { return this.$store.getters.isLoggedIn; },
    userInfo() { return this.$store.getters.userInfo; },
    userCredits() { return this.$store.getters.userCredits; },
    planName() { return this.$store.getters.planName; },
    lowCredits() { return this.userCredits < 15; },
    initial() { return (this.userInfo?.username || 'G').charAt(0).toUpperCase(); },
    createItems() {
      return [
        { to: '/faceswap', emoji: '🎭', label: this.$t('routes.faceswap'), isNew: false },
        { to: '/headswap', emoji: '🗣️', label: 'Head Swap', isNew: true },
        { to: '/character-animate', emoji: '🥷', label: 'Character Swap & Animate Image', isNew: true },
        { to: '/imagine-studio', emoji: '✨', label: this.$t('routes.imagineStudio'), isNew: false },
        { to: '/image-restore', emoji: '🖼️', label: this.$t('routes.imageRestore'), isNew: false },
        { to: '/slow-motion', emoji: '🎬', label: this.$t('routes.slowMotion'), isNew: false },
        { to: '/studio', emoji: '🎞️', label: this.$t('routes.studio'), isNew: false },
        { divider: true, to: '#d1' },
        { to: '/gallery', emoji: '🗂️', label: this.$t('routes.gallery'), isNew: false },
        { to: '/subscriptions', emoji: '🛒', label: this.$t('routes.subscription'), isNew: false },
        { to: '/support-center', emoji: '🎧', label: this.$t('routes.support'), isNew: false },
      ];
    },
    createActive() {
      return this.createItems.some((i) => !i.divider && i.to === this.$route.path);
    },
    currentLangCode() {
      const code = (this.$i18n && this.$i18n.locale) || 'en';
      const map = { 'zh-Hans': 'ZH' };
      return map[code] || code.slice(0, 2).toUpperCase();
    },
  },
  methods: {
    logout() {
      this.closeDrawer();
      this.$store.dispatch('logout').then(() => this.$router.push('/login'));
    },
    openDrawer() { this.drawerOpen = true; document.body.style.overflow = 'hidden'; },
    closeDrawer() { this.drawerOpen = false; document.body.style.overflow = ''; },
    onKey(e) {
      if (e.key === 'Escape') { this.createOpen = false; this.profileOpen = false; this.closeDrawer(); }
    },
    onDocClick(e) {
      if (this.createOpen && this.$refs.createWrap && !this.$refs.createWrap.contains(e.target)) this.createOpen = false;
      if (this.profileOpen && this.$refs.profileWrap && !this.$refs.profileWrap.contains(e.target)) this.profileOpen = false;
    },
    onScroll() { this.scrolled = window.scrollY > 8; },
    async fetchSupportUnread() {
      try {
        const r = await this.$apiClient.get('/support/tickets/');
        const t = r.data?.tickets || r.data || [];
        this.supportUnreadCount = Array.isArray(t) ? t.filter(x => x.unread_by_user).length : 0;
      } catch (e) { /* silent */ }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('click', this.onDocClick);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.loggedIn) {
      this.$store.dispatch('getUserCredits');
      this.fetchSupportUnread();
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('scroll', this.onScroll);
    document.body.style.overflow = '';
  },
};
</script>

<style scoped>
/* ============ bar ============ */
.nv2 { position: fixed; top: 0; left: 0; right: 0; z-index: 9999; background: rgba(7, 4, 15, 0.7); backdrop-filter: blur(14px); border-bottom: 1px solid rgba(124, 58, 237, 0.18); transition: background 0.2s; }
.nv2--scrolled { background: rgba(7, 4, 15, 0.92); box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35); }
.nv2-bar { max-width: 1200px; margin: 0 auto; padding: 0 16px; height: 62px; display: flex; align-items: center; gap: 10px; }
.nv2-logo img { width: 116px; height: auto; display: block; }
.nv2-spacer { flex: 1; }

/* ============ desktop nav ============ */
.nv2-desktop { display: none; align-items: center; gap: 4px; margin-left: 18px; }
.nv2-link { color: rgba(255, 255, 255, 0.78); font-size: 14px; padding: 8px 14px; border-radius: 10px; text-decoration: none; background: transparent; border: none; cursor: pointer; line-height: 1.2; display: inline-flex; align-items: center; gap: 6px; }
.nv2-link:hover { color: #fff; background: rgba(124, 58, 237, 0.16); }
.nv2-link--on { color: #fff; background: linear-gradient(90deg, rgba(124, 58, 237, 0.35), rgba(236, 72, 153, 0.25)); }
.nv2-caret { font-size: 11px; transition: transform 0.18s; }
.nv2-caret--open { transform: rotate(180deg); }

/* ============ popovers ============ */
.nv2-menu-wrap { position: relative; }
.nv2-pop { position: absolute; top: calc(100% + 10px); left: 0; min-width: 250px; background: #120c22; border: 1px solid rgba(168, 85, 247, 0.35); border-radius: 16px; padding: 8px; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.55); list-style: none; margin: 0; }
.nv2-pop--right { left: auto; right: 0; }
.nv2-pop-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px; border-radius: 10px; color: #d8d2ea; font-size: 13.5px; text-decoration: none; background: transparent; border: none; cursor: pointer; text-align: left; line-height: 1.3; box-sizing: border-box; }
.nv2-pop-item:hover { background: rgba(124, 58, 237, 0.18); color: #fff; }
.nv2-pop-item--on { background: linear-gradient(90deg, rgba(124, 58, 237, 0.3), rgba(236, 72, 153, 0.18)); color: #fff; }
.nv2-pop-label { flex: 1; }
.nv2-emoji { width: 22px; text-align: center; }
.nv2-new { font-size: 9px; font-weight: 800; letter-spacing: 0.05em; color: #fff; background: linear-gradient(90deg, #7c3aed, #ec4899); border-radius: 5px; padding: 2px 6px; }
.nv2-sep { border: none; height: 1px; background: rgba(124, 58, 237, 0.28); margin: 8px 6px; }

/* ============ right cluster ============ */
.nv2-right { display: flex; align-items: center; gap: 8px; }
.nv2-credits { display: inline-flex; align-items: center; gap: 6px; padding: 7px 13px; border-radius: 999px; text-decoration: none; color: #fff; font-size: 13.5px; background: linear-gradient(90deg, rgba(124, 58, 237, 0.4), rgba(236, 72, 153, 0.3)); border: 1px solid rgba(168, 85, 247, 0.45); line-height: 1; }
.nv2-credits b { font-weight: 800; }
.nv2-credits--low { background: linear-gradient(90deg, rgba(245, 158, 11, 0.3), rgba(217, 119, 6, 0.25)); border-color: rgba(245, 158, 11, 0.55); }
.nv2-topup { font-style: normal; font-size: 11px; font-weight: 800; color: #fbbf24; text-transform: uppercase; letter-spacing: 0.04em; }
.nv2-icon-btn { position: relative; display: none; align-items: center; gap: 5px; padding: 7px 11px; border-radius: 999px; background: rgba(124, 58, 237, 0.14); border: 1px solid rgba(124, 58, 237, 0.3); color: #d8d2ea; font-size: 14px; cursor: pointer; text-decoration: none; line-height: 1; }
.nv2-icon-btn:hover { color: #fff; border-color: rgba(168, 85, 247, 0.6); }
.nv2-lang-code { font-size: 11.5px; font-weight: 700; }
.nv2-badge { position: absolute; top: -5px; right: -5px; min-width: 17px; height: 17px; border-radius: 999px; background: #ec4899; color: #fff; font-size: 10px; font-weight: 800; display: grid; place-items: center; padding: 0 4px; }
.nv2-avatar-btn { width: 38px; height: 38px; border-radius: 50%; border: 2px solid rgba(168, 85, 247, 0.5); background: linear-gradient(135deg, #7c3aed, #ec4899); color: #fff; font-weight: 800; font-size: 15px; cursor: pointer; line-height: 1; }

/* account card */
.nv2-acct { display: flex; align-items: center; gap: 10px; padding: 12px; border-bottom: 1px solid rgba(124, 58, 237, 0.25); margin-bottom: 6px; }
.nv2-acct-avatar { width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; display: grid; place-items: center; font-weight: 800; font-size: 16px; color: #fff; background: linear-gradient(135deg, #7c3aed, #ec4899); }
.nv2-acct-info { min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.nv2-acct-info b { color: #fff; font-size: 14px; line-height: 1.2; max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.nv2-acct-meta { display: flex; align-items: center; gap: 8px; }
.nv2-plan { font-style: normal; font-size: 10px; font-weight: 800; letter-spacing: 0.05em; color: #fff; background: linear-gradient(90deg, #7c3aed, #a855f7); border-radius: 6px; padding: 2px 8px; text-transform: uppercase; }
.nv2-acct-cr { font-style: normal; font-size: 12px; color: #e9d5ff; text-decoration: none; }
.nv2-acct-cr--low { color: #fbbf24; font-weight: 700; }
.nv2-promo { display: flex; align-items: center; gap: 8px; margin: 8px 6px; padding: 10px 12px; border-radius: 10px; background: linear-gradient(90deg, #ea580c, #f59e0b); color: #fff; font-size: 12.5px; font-weight: 700; text-decoration: none; line-height: 1.3; }
.nv2-logout { color: #f87171; }
.nv2-promo--greazy { background: linear-gradient(90deg, #d946ef 0%, #2dd4bf 50%, #f59e0b 100%); color: #12021c; }

/* ============ mobile ============ */
.nv2-mobile-only { display: inline-flex; }
.nv2-burger { width: 40px; height: 40px; border-radius: 10px; background: rgba(124, 58, 237, 0.14); border: 1px solid rgba(124, 58, 237, 0.3); display: inline-flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; cursor: pointer; }
.nv2-burger span { display: block; width: 17px; height: 2px; border-radius: 2px; background: #fff; }

.nv2-veil { position: fixed; inset: 0; background: rgba(3, 0, 12, 0.6); backdrop-filter: blur(3px); z-index: 10000; }
.nv2-drawer { position: absolute; top: 0; right: 0; bottom: 0; width: min(320px, 88vw); background: #0d0819; border-left: 1px solid rgba(168, 85, 247, 0.35); padding: 14px 14px 24px; overflow-y: auto; overflow-x: hidden; display: flex; flex-direction: column; box-sizing: border-box; }
.nv2-drawer-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.nv2-drawer-logo { width: 104px; height: auto; }
.nv2-x { width: 36px; height: 36px; border-radius: 10px; background: rgba(124, 58, 237, 0.16); border: 1px solid rgba(124, 58, 237, 0.35); color: #fff; font-size: 15px; cursor: pointer; }
.nv2-acct--drawer { border: 1px solid rgba(124, 58, 237, 0.3); border-radius: 14px; background: linear-gradient(120deg, rgba(124, 58, 237, 0.22), rgba(16, 13, 32, 0.9)); margin-bottom: 12px; }
.nv2-drawer-title { font-size: 11px; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase; color: #9b8bc4; margin: 4px 6px 6px; }
.nv2-drawer-list { display: flex; flex-direction: column; gap: 2px; }
.nv2-drawer-item { display: flex; align-items: center; gap: 12px; width: 100%; padding: 12px; min-height: 44px; border-radius: 12px; color: #d8d2ea; font-size: 14px; text-decoration: none; background: transparent; border: none; cursor: pointer; text-align: left; box-sizing: border-box; }
.nv2-drawer-item:hover, .nv2-drawer-item--on { background: rgba(124, 58, 237, 0.18); color: #fff; }
.nv2-drawer-item--on { background: linear-gradient(90deg, rgba(124, 58, 237, 0.3), rgba(236, 72, 153, 0.18)); }
.nv2-promo--drawer { margin: 12px 2px; }

/* transitions */
.nv2-pop-enter-active, .nv2-pop-leave-active { transition: opacity 0.14s, transform 0.14s; }
.nv2-pop-enter-from, .nv2-pop-leave-to { opacity: 0; transform: translateY(-6px); }
.nv2-fade-enter-active, .nv2-fade-leave-active { transition: opacity 0.18s; }
.nv2-fade-enter-from, .nv2-fade-leave-to { opacity: 0; }

/* ============ breakpoints ============ */
@media (min-width: 1024px) {
  .nv2-desktop { display: flex; }
  .nv2-desktop-only.nv2-icon-btn, .nv2-icon-btn.nv2-desktop-only { display: inline-flex; }
  .nv2-menu-wrap.nv2-desktop-only { display: block; }
  .nv2-mobile-only { display: none; }
  .nv2-bar { height: 68px; }
}
@media (max-width: 1023.9px) {
  .nv2-desktop-only { display: none !important; }
}
</style>
